import { createContext } from "react";
import { FETCH_MODAL_STACK_Z_STEP } from "./constants";

type CloseHandler = () => void;

// Effective z-index of every open FetchModal, keyed by the close handler it registers on the
// shared modal stack. Other overlays on that stack (pickers, the agenda planner) have no entry.
const layerZIndexByClose = new Map<CloseHandler, number>();

/**
 * Records the z-index an open modal actually renders at, so a modal opened on top of it can be
 * placed above it.
 * @param close - The modal's stable close handler (its identity on the modal stack).
 * @param zIndex - The backdrop z-index the modal renders at.
 * @returns Nothing.
 */
export function registerModalLayer(close: CloseHandler, zIndex: number): void {
  layerZIndexByClose.set(close, zIndex);
}

/**
 * Forgets a modal's layer once it closes.
 * @param close - The close handler passed to {@link registerModalLayer}.
 * @returns Nothing.
 */
export function unregisterModalLayer(close: CloseHandler): void {
  layerZIndexByClose.delete(close);
}

/**
 * Finds the z-index of the FetchModal directly under `self` on the stack: the topmost one when
 * `self` is not on the stack yet (it is opening), otherwise the nearest one below it. Stack
 * entries that are not FetchModals are skipped.
 * @param stack - The shared modal stack, bottom first.
 * @param self - The modal asking, which is never its own parent.
 * @returns The parent modal's z-index, or null when no FetchModal is under `self`.
 */
export function getParentModalZIndex(stack: ReadonlyArray<CloseHandler>, self: CloseHandler): number | null {
  const selfIndex = stack.lastIndexOf(self);
  const startIndex = selfIndex === -1 ? stack.length - 1 : selfIndex - 1;
  for (let index = startIndex; index >= 0; index -= 1) {
    const entry = stack[index];
    const zIndex = layerZIndexByClose.get(entry);
    if (zIndex !== undefined) return zIndex;
  }
  return null;
}

/**
 * Resolves the z-index a modal renders at so it is never under the modal it was opened from.
 * A requested value at or above the parent's is kept; a lower one is lifted
 * {@link FETCH_MODAL_STACK_Z_STEP} above the parent.
 * @param requested - The caller's `zIndex` prop.
 * @param parentZIndex - The parent modal's z-index, or null when this is the only modal.
 * @returns The z-index to render the backdrop at (the panel renders one above it).
 */
export function resolveStackedZIndex(requested: number, parentZIndex: number | null): number {
  if (parentZIndex === null || requested >= parentZIndex) return requested;
  return parentZIndex + FETCH_MODAL_STACK_Z_STEP;
}

/**
 * The panel z-index of the nearest enclosing FetchModal, or null outside any modal. Read it with
 * `useModalLayerZIndex`.
 */
export const FetchModalLayerContext = createContext<number | null>(null);
